import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import i18n from "i18next";
import { Alert, Button, Modal, Progress, Space, Typography } from "antd";
import { SkosRepository, SkosScheme } from "../../redux";

const { Paragraph, Text, Title } = Typography;

function DeleteScheme({ open, onClose, onDeleted, dispatch, uri }) {
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDone, setIsDone] = useState(false);
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState("");
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setIsDeleting(false);
    setIsDone(false);
    setProgress(0);
    setStatus("");
    setError(null);
  }, [open, uri]);

  async function handleDelete() {
    setIsDeleting(true);
    setIsDone(false);
    setProgress(0);
    setError(null);

    try {
      setStatus(i18n.t("components.skos.deleteScheme.loadingScheme"));
      await dispatch(SkosScheme.loadScheme(uri));

      setStatus(i18n.t("components.skos.deleteScheme.loadingConcepts"));
      const response = await dispatch(SkosScheme.loadInScheme(uri));
      const concepts = uniqueConceptIRIs(response?.results?.bindings);
      const total = Math.max(concepts.length + 1, 1);

      for (let index = 0; index < concepts.length; index += 1) {
        setStatus(i18n.t("components.skos.deleteScheme.deletingConcept", { current: index + 1, total: concepts.length }));
        await dispatch(SkosRepository.deleteConcept(concepts[index]));
        setProgress(Math.round(((index + 1) / total) * 100));
      }

      setStatus(i18n.t("components.skos.deleteScheme.deletingScheme"));
      await dispatch(SkosRepository.deleteElement(uri));
      setProgress(100);
      setStatus(i18n.t("components.skos.deleteScheme.deleted"));
      setIsDone(true);
      onDeleted?.({ uri, concepts });
    } catch (e) {
      setError(e?.message || String(e));
      setStatus("");
    } finally {
      setIsDeleting(false);
    }
  }

  function handleClose() {
    if (!isDeleting) onClose?.();
  }

  return (
    <Modal
      title={<Title level={5} style={{ margin: 0 }}>{i18n.t("components.skos.deleteScheme.title")}</Title>}
      open={open}
      onCancel={handleClose}
      closable={!isDeleting}
      maskClosable={!isDeleting}
      footer={<Space>
        {!isDone ? <Button onClick={handleClose} disabled={isDeleting}>{i18n.t("components.skos.deleteScheme.cancel")}</Button> : null}
        <Button danger={!isDone} type="primary" loading={isDeleting}
          disabled={isDeleting || !uri}
          onClick={isDone ? handleClose : handleDelete}>
          {i18n.t(isDone ? "components.skos.deleteScheme.close" : "components.skos.deleteScheme.delete")}
        </Button>
      </Space>}
    >
      <Space orientation="vertical" size="middle" style={{ width: "100%" }}>
        <Alert type="warning" showIcon message={i18n.t("components.skos.deleteScheme.irreversible")}
          description={<Paragraph style={{ marginBottom: 0 }}>
           {i18n.t("components.skos.deleteScheme.warning")}
          </Paragraph>}/>
        <Text copyable>{uri}</Text>
        {isDeleting || isDone ? <Space orientation="vertical" style={{ width: "100%" }}>
          <Progress percent={progress} status={isDone ? "success" : "active"}/>
          {status ? <Text>{status}</Text> : null}
        </Space> : null}
        {error ? <Alert type="error" showIcon message={i18n.t("components.skos.deleteScheme.deleteError")} description={error}/> : null}
      </Space>
    </Modal>
  );
}

function uniqueConceptIRIs(bindings) {
  return [...new Set((Array.isArray(bindings) ? bindings : [])
    .map(item => item.IRI?.value)
    .filter(Boolean))];
}

const mapStateToProps = (state, ownProps) => ({
  ...ownProps,
  uri: ownProps.uri ?? ownProps.IRI ?? ownProps.cURI,
});

export default connect(mapStateToProps)(DeleteScheme);
